import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CodeBracketIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useAdminSettings, useUpdateSetting } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';

const SETTINGS_DISPLAY_MAP: Record<string, { labelKey: string; descriptionKey: string }> = {
  ai_enabled: { labelKey: 'adminSettings.aiEnabled', descriptionKey: 'adminSettings.aiEnabledDesc' },
  ai_default_model: { labelKey: 'adminSettings.aiModel', descriptionKey: 'adminSettings.aiModelDesc' },
  ai_max_tokens: { labelKey: 'adminSettings.aiMaxTokens', descriptionKey: 'adminSettings.aiMaxTokensDesc' },
  maintenance_mode: { labelKey: 'adminSettings.maintenance', descriptionKey: 'adminSettings.maintenanceDesc' },
  app_version_ios: { labelKey: 'adminSettings.versionIos', descriptionKey: 'adminSettings.versionIosDesc' },
  app_version_panel: { labelKey: 'adminSettings.versionPanel', descriptionKey: 'adminSettings.versionPanelDesc' },
  bridge_min_version: { labelKey: 'adminSettings.bridgeVersion', descriptionKey: 'adminSettings.bridgeVersionDesc' },
  cache_ttl_dashboard: { labelKey: 'adminSettings.cacheDashboard', descriptionKey: 'adminSettings.cacheDashboardDesc' },
  cache_ttl_orders: { labelKey: 'adminSettings.cacheOrders', descriptionKey: 'adminSettings.cacheOrdersDesc' },
  cache_ttl_products: { labelKey: 'adminSettings.cacheProducts', descriptionKey: 'adminSettings.cacheProductsDesc' },
  subscription_grace_days: { labelKey: 'adminSettings.graceDays', descriptionKey: 'adminSettings.graceDaysDesc' },
  subscription_notify_days: { labelKey: 'adminSettings.notifyDays', descriptionKey: 'adminSettings.notifyDaysDesc' },
};

export function AdminSettingsPage() {
  const { t } = useTranslation();
  const { data: settings, isLoading, isError, refetch } = useAdminSettings();
  const updateSetting = useUpdateSetting();
  const [editSetting, setEditSetting] = useState<{ key: string; value: string; label: string; description: string } | null>(null);

  const getLabel = (key: string) => {
    const map = SETTINGS_DISPLAY_MAP[key];
    return map ? t(map.labelKey) : key;
  };

  const getDescription = (key: string) => {
    const map = SETTINGS_DISPLAY_MAP[key];
    return map ? t(map.descriptionKey) : '';
  };

  const handleSave = async () => {
    if (!editSetting) return;
    await updateSetting.mutateAsync({ key: editSetting.key, value: editSetting.value });
    setEditSetting(null);
  };

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.appSettings')}</h1>

      <Card>
        {isLoading ? (
          <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}</div>
        ) : settings?.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {settings.map((s: { key: string; value: unknown; description?: string }) => (
              <div key={s.key} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{getLabel(s.key)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{getDescription(s.key) || s.description || ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="max-w-xs truncate text-sm text-gray-600 dark:text-gray-300">{typeof s.value === 'object' ? JSON.stringify(s.value) : String(s.value)}</span>
                  <Button variant="ghost" size="sm" onClick={() => setEditSetting({ key: s.key, value: typeof s.value === 'object' ? JSON.stringify(s.value) : String(s.value), label: getLabel(s.key), description: getDescription(s.key) || s.description || '' })}>{t('common.edit')}</Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">-</p>
        )}
      </Card>

      <Card>
        <a
          href="https://api.wooconnectapp.com/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <CodeBracketIcon className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('adminSettings.apiDocsTitle')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('adminSettings.apiDocsDesc')}</p>
            </div>
          </div>
          <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
        </a>
      </Card>

      <Modal isOpen={!!editSetting} onClose={() => setEditSetting(null)} title={t('admin.editSetting')} size="sm">
        {editSetting && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{editSetting.label}</p>
              {editSetting.description && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{editSetting.description}</p>}
            </div>
            <Input label={t('admin.settingValue')} value={editSetting.value} onChange={(e) => setEditSetting((s) => s ? { ...s, value: e.target.value } : null)} />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setEditSetting(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} loading={updateSetting.isPending}>{t('common.save')}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
